import { useMemo } from 'react'
import { TRANSFER_MODES, TransferMode } from './transferTypes'

export interface TransferEntity {
  parentId?: string
}

interface UseTransferDataForTransferParams<T extends TransferEntity> {
  vehiclesData: T[]
  driversData: T[]
  usersData: T[]
  geosData: T[]
  accountsData: T[]
  retranslationsData: T[]
  selectedSubpartner: string
  userSelectedSubpartner: boolean
  transferMode: TransferMode
  shouldFilterByParentId: boolean
}

export interface TransferDataForTransfer<T extends TransferEntity> {
  objectsDataForTransfer: T[]
  usersDataForTransfer: T[]
  geozonesDataForTransfer: T[]
  driversDataForTransfer: T[]
  accountsDataForTransfer: T[]
  retranslationsDataForTransfer: T[]
}

const filterByParent = <T extends TransferEntity>(
  data: T[],
  parentId: string
) => data.filter((item) => item.parentId === parentId)

export const useTransferDataForTransfer = <T extends TransferEntity>({
  vehiclesData,
  driversData,
  usersData,
  geosData,
  accountsData,
  retranslationsData,
  selectedSubpartner,
  userSelectedSubpartner,
  transferMode,
  shouldFilterByParentId,
}: UseTransferDataForTransferParams<T>): TransferDataForTransfer<T> => {
  const shouldFilterBySubpartner =
    transferMode === TRANSFER_MODES.ENTITIES &&
    userSelectedSubpartner &&
    !!selectedSubpartner &&
    shouldFilterByParentId

  // Сохраняем исторически иной (без userSelectedSubpartner) критерий фильтрации ретрансляций
  const shouldFilterRetranslations =
    transferMode === TRANSFER_MODES.ENTITIES &&
    !!selectedSubpartner &&
    shouldFilterByParentId

  const objectsDataForTransfer = useMemo(
    () =>
      shouldFilterBySubpartner
        ? filterByParent(vehiclesData, selectedSubpartner)
        : vehiclesData,
    [shouldFilterBySubpartner, vehiclesData, selectedSubpartner]
  )

  const driversDataForTransfer = useMemo(
    () =>
      shouldFilterBySubpartner
        ? filterByParent(driversData, selectedSubpartner)
        : driversData,
    [shouldFilterBySubpartner, driversData, selectedSubpartner]
  )

  const usersDataForTransfer = useMemo(
    () =>
      shouldFilterBySubpartner
        ? filterByParent(usersData, selectedSubpartner)
        : usersData,
    [shouldFilterBySubpartner, usersData, selectedSubpartner]
  )

  const geozonesDataForTransfer = useMemo(
    () =>
      shouldFilterBySubpartner
        ? filterByParent(geosData, selectedSubpartner)
        : geosData,
    [shouldFilterBySubpartner, geosData, selectedSubpartner]
  )

  const accountsDataForTransfer = useMemo(() => {
    if (transferMode === TRANSFER_MODES.FULL) return accountsData
    return shouldFilterBySubpartner
      ? filterByParent(accountsData, selectedSubpartner)
      : accountsData
  }, [transferMode, shouldFilterBySubpartner, accountsData, selectedSubpartner])

  const retranslationsDataForTransfer = useMemo(
    () =>
      shouldFilterRetranslations
        ? filterByParent(retranslationsData, selectedSubpartner)
        : retranslationsData,
    [shouldFilterRetranslations, retranslationsData, selectedSubpartner]
  )

  return {
    objectsDataForTransfer,
    usersDataForTransfer,
    geozonesDataForTransfer,
    driversDataForTransfer,
    accountsDataForTransfer,
    retranslationsDataForTransfer,
  }
}
